import MobxReactForm from 'mobx-react-form';
import validatorjs from 'validatorjs';
import dvr from 'mobx-react-form/lib/validators/DVR';
import vjf from 'mobx-react-form/lib/validators/VJF';
import { action, observable, makeObservable } from 'mobx';

const DEFAULT_PLUGINS = {
  dvr: dvr(validatorjs),
  vjf: vjf(),
};

const DEFAULT_HOOKS = {
  onSuccess(form: any): void {
    form.onSuccess(form);
  },
  onError(form: any): void {
    if (form.onError) {
      form.onError(form);
    }
  },
};

interface FormType {
  fields: any[];
}

type Validators = (args: any) => [boolean, string];

export type FieldsType = Readonly<{
  [field: string]: {
    rules?: string | Readonly<string[]>;
    label?: string;
    placeholder?: string;
    extra?: { hint: string };
    related?: Readonly<string[]>;
    validators?: Readonly<Validators[]>;
  };
}>;

/**
 * `mobx-react-form` uses the non-decorator `makeObservable` form of setting object properties.
 *
 * Because of this, any classes that extend from `BaseFormObject` *must* also use the non-decorator
 * form, otherwise `mobx` will throw a runtime error.
 *
 * @see https://github.com/curology/PocketDerm/pull/15206#pullrequestreview-806599603
 */
class BaseFormObject<Fields extends FieldsType> extends MobxReactForm {
  constructor(
    setup: { fields: Fields },
    plugins = DEFAULT_PLUGINS,
    hooks = DEFAULT_HOOKS,
  ) {
    super(setup, { plugins, hooks });

    makeObservable(this, {
      hasTouchedAll: observable,
      onError: action,
      handleReset: action,
    });
  }

  hasTouchedAll = false;

  onError(form: FormType): void {
    // Note: The following block ensures that all errors will render on
    // submission of an empty form that has not been interacted with
    if (!this.hasTouchedAll) {
      form.fields.forEach((field) => {
        // eslint-disable-next-line no-param-reassign
        field.$touched = true;
      });
      this.hasTouchedAll = true;
    }
  }

  handleReset = (): void => {
    // Set fields to untouched
    this.fields.forEach((field: any) => {
      // eslint-disable-next-line no-param-reassign
      field.$touched = false;
    });
    this.hasTouchedAll = false;

    this.reset();
  };

  options(): { validateOnChange: true } {
    return {
      validateOnChange: true,
    };
  }
}

export default BaseFormObject;
